<template>
  <table class="table-hover reports-table amounts-owned-table table">
    <tbody class="tbody thead">
      <tr>
        <th v-if="tableIsTeam">Member</th>
        <th v-if="tableIsTeam && tableShowAboutMember">
          <AppToggleDropdown
            trigger-classes="color-inherit"
            dropdown-classes="table-actions-dropdown"
            extra-classes="member-dropdown-header"
          >
            <template #trigger>
              <span class="clickable hover-visible-container">
                About
                <i class="hi-edit hover-visible text-blue ml-10" />
              </span>
            </template>
            <MembersAboutFields
              :about-fields="aboutFields"
              extra-fields
              custom-fields
              custom-visible-by-default
              @change="handleAboutChange"
            />
          </AppToggleDropdown>
        </th>
        <th v-if="tableShowProjects">
          {{ $filters.projectFlavourLabel(organization.project_flavour) }}
        </th>
        <th class="text-right">Current rate</th>
        <th
          v-if="showOvertime"
          class="text-right"
        >
          Regular hours
          <ReportTableTooltipIcon
            v-if="workBreaksEnabled"
            message="Regular hours includes work break time. Overtime calculations exclude work break time."
          />
        </th>
        <th
          v-if="showOvertime"
          class="text-right"
        >
          Overtime
        </th>
        <th class="text-right">Total hours</th>
        <th class="text-right">Amount</th>
      </tr>
    </tbody>
    <!--eslint-disable vue/require-v-for-key -->
    <template v-for="(section, index) in tableData.sections">
      <tbody class="grouping-header">
        <tr v-if="showGroupingRow">
          <th colspan="100%">
            {{ formatDateLabel(section.main) }}
          </th>
        </tr>
      </tbody>

      <!--eslint-disable vue/valid-v-for -->
      <ReportVirtualTableBody
        v-slot="{ row }"
        :rows="section.rows"
      >
        <tr>
          <td v-if="tableIsTeam && walker.sub_show">
            <template v-if="row.sub_show_data">
              <MemberPaymentAccountPopup
                :organization-id="organization.id"
                :user-id="row.record.sub_id"
                :image-url="row.record.avatar_url"
                :name="row.record.sub"
                :show-member-profile="managePaymentInfo"
                tab="pay / bill"
                title=""
              />
            </template>
          </td>
          <td v-if="tableIsTeam && tableShowAboutMember">
            <MemberAboutData
              :about-fields="aboutFields"
              :member-data="row.record"
              :user-id="row.record.sub_id"
              :user-name="row.record.sub"
              custom-visible-by-default
            />
          </td>

          <template v-if="walker.third_show">
            <td class="project-cell">
              <template v-if="row.third_show_data && row.record.third">{{ row.record.third }} </template>
            </td>
          </template>

          <template v-if="allRatesMissing(row)">
            <td class="text-right"><small>No rate set</small></td>
          </template>
          <template v-else-if="mixedRates(row)">
            <td class="text-right">
              Mixed
              <ReportTableTooltipIcon
                data-html
                :message="mixedRateListTooltip(row)"
                :type="missingRates(row) ? 'danger' : 'info'"
              />
            </td>
          </template>
          <template v-else>
            <td class="text-right">{{ $filters.currency(currentRate(row), organization.currency) }}</td>
          </template>

          <td
            v-if="showOvertime"
            class="text-right"
          >
            <span v-if="!!row.record.tracked">
              {{ formatDuration(row.record.regular_tracked, { blank: '—' }) }}
            </span>
            <span v-else>--</span>
          </td>

          <td
            v-if="showOvertime"
            class="text-right"
          >
            <template v-if="!!row.record.overtime">
              <ReportOvertimePopover
                :overtime="getOvertimeBreakdown(row)"
                :title="flexibleOvertime ? 'Overtime' : 'Weekly overtime'"
                :currency="organization.currency"
                :show-financial="isOwner"
              />
              {{ $filters.formatDuration(row.record.overtime, 'auto', null, false, true) }}
            </template>
            <span v-else>--</span>
          </td>

          <td
            v-if="row.aggregate && row.aggregate.tracked"
            class="text-right"
          >
            <ReportTableTooltipIcon
              data-html
              :class="recordTypeIcon(row).class"
              :message="recordTypeIcon(row).text"
            />
            <b>{{ $filters.formatDuration(row.aggregate.tracked, 'auto', null, false, true) }}</b>
          </td>
          <td
            v-else-if="row.record.tracked"
            class="text-right"
          >
            <ReportTableTooltipIcon
              data-html
              :class="recordTypeIcon(row).class"
              :message="recordTypeIcon(row).text"
            />
            {{ $filters.formatDuration(row.record.tracked, 'auto', null, false, true) }}
          </td>

          <td
            v-if="row.aggregate && row.aggregate.amount"
            class="text-right"
          >
            <b>{{ $filters.currency(row.aggregate.amount, organization.currency) }}</b>
          </td>
          <td
            v-else-if="row.record.amount"
            class="text-right"
          >
            {{ currency(row.record.amount) }}
          </td>
        </tr>
      </ReportVirtualTableBody>
      <!--eslint-enable vue/valid-v-for -->

      <tbody
        v-if="showLastFooter(index)"
        class="tfoot"
      >
        <tr>
          <td v-if="tableIsTeam"><b>Total</b></td>
          <td v-if="tableIsTeam && tableShowAboutMember">&nbsp;</td>
          <td
            v-if="tableIsTeam && walker.third_show"
            class="project-cell"
          >
            &nbsp;
          </td>
          <td
            v-if="!tableIsTeam && walker.third_show"
            class="project-cell"
          >
            <b>Total</b>
          </td>
          <td v-if="!tableIsTeam && walker.third_show">&nbsp;</td>
          <td v-if="tableIsTeam && tableShowAboutMember">&nbsp;</td>
          <td
            v-if="(tableIsTeam && !tableShowAboutMember) || (!tableIsTeam && !tableShowProjects)"
            class="text-right"
          ></td>
          <td
            v-if="showOvertime"
            class="text-right"
          >
            <span v-if="!!section.total.tracked">
              {{ formatDuration(section.total.regular_tracked, { blank: '—' }) }}
            </span>
            <span v-else>--</span>
          </td>
          <td
            v-if="showOvertime"
            class="text-right"
          >
            <span v-if="!!section.total.overtime">
              {{ $filters.formatDuration(section.total.overtime, 'auto', null, false, true) }}
            </span>
            <span v-else>--</span>
          </td>
          <td class="text-right">
            <strong>{{ $filters.formatDuration(section.total.tracked, 'auto', null, false, true) }}</strong>
          </td>
          <td class="text-right">
            <strong>{{ currency(section.total.amount) }}</strong>
          </td>
        </tr>
      </tbody>
    </template>
    <!--eslint-enable vue/require-v-for-key -->
  </table>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { escape as htmlEscape, groupBy, map, sumBy } from 'lodash-es';
import currency from '~/filters/currency';
import ReportOvertimePopover from '~/components/reports/ReportOvertimePopover.vue';
import MemberAboutData from '~/components/members/MemberAboutData.vue';
import MembersAboutFields from '~/components/members/MembersAboutFields.vue';
import MemberPaymentAccountPopup from '~/components/financials/MemberPaymentAccountPopup.vue';
import { formatDuration } from '~/utils/time_utils';

export default {
  name: 'AmountsOwedReportTable',
  components: {
    ReportOvertimePopover,
    MemberAboutData,
    MembersAboutFields,
    MemberPaymentAccountPopup,
  },
  props: {
    tableData: {
      type: Object,
      default: () => {},
    },
    showOvertime: { type: Boolean, default: false },
  },
  emits: ['change'],
  computed: {
    ...mapGetters([
      'tableIsTeam',
      'walker',
      'tableShowProjects',
      'tableShowAboutMember',
      'aboutFields',
      'isOwner',
      'workBreaksEnabled',
      'managePaymentInfo',
    ]),
    ...mapState({
      organization: 'organization',
      members: 'members',
      projects: 'projects',
      tasks: 'tasks',
      report_query: 'report_query',
      previous_report_query: 'previous_report_query',
      pagination: 'pagination',
    }),
    showGroupingRow() {
      return !!(this.walker.base_column_count - (this.tableIsTeam ? 0 : 1));
    },
    flexibleOvertime() {
      return this.organization.flags?.includes('flexible_overtime') ?? false;
    },
  },
  methods: {
    formatDuration,
    showLastFooter(index) {
      return index !== this.tableData.sections.length - 1 || !this.pagination.next_page;
    },
    currency(amount) {
      return currency(amount, this.organization.currency);
    },
    getProjectRate: function (projectRates, projectId) {
      const projectRate = projectRates.find((e) => '' + projectId in (e || {}));

      if (projectRate) {
        return projectRate[projectId];
      }

      return 0;
    },
    getOvertimeBreakdown({ record }) {
      if (this.flexibleOvertime) {
        const buildTypedEntries = (breakdown, payRates, label) => {
          if (!breakdown?.length) { return []; }

          const byRate = {};

          breakdown.forEach((row) => {
            const [projectId, overtime] = Object.entries(row)[0] || [];
            const rate = this.getProjectRate(payRates || [], projectId);
            const key = rate !== 0 ? rate : 'none';

            byRate[key] = { rate: rate || false, overtime: (byRate[key]?.overtime || 0) + overtime };
          });

          return Object.values(byRate).map(({ rate, overtime }) => ({ overtime, overtimeRate: rate, label }));
        };

        return [
          ...buildTypedEntries(record.daily_overtime_breakdown, record.daily_pay_rates, 'Daily'),
          ...buildTypedEntries(record.weekly_overtime_breakdown, record.weekly_pay_rates, 'Weekly'),
        ];
      }

      const { overtime_breakdown, pay_rates } = record;
      const breakdown = overtime_breakdown
        .map((row) => {
          // { "project_id" => overtime }
          const [projectId, overtime] = Object.entries(row)[0] || [];

          return overtime && { overtime, pay_rate: this.getProjectRate(pay_rates, projectId) };
        })
        .filter(Boolean);
      const breakdownByPayrate = groupBy(breakdown, 'pay_rate');
      const breakdownSumByPayrate = map(breakdownByPayrate, (rows, pay_rate) => ({
        overtime: sumBy(rows, 'overtime'),
        overtimeRate: parseFloat(pay_rate),
      }));

      return breakdownSumByPayrate;
    },
    allRatesMissing: function (row) {
      // Check if there's no rate set for any of the project and no rates as base.
      const baseRates = row.record.base_rates.map((e) => Object.values(e)).flat();
      const projectRates = row.record.project_rates.map((e) => Object.values(e)).flat();
      const taskRates = row.record.task_rates?.map((e) => Object.values(e))?.flat() || [];
      const nonEmptyRate = [...baseRates, ...projectRates, ...taskRates].find((e) => e);

      return !nonEmptyRate;
    },
    missingRates: function (row) {
      // Check if there's no base rate for that day neither rates for the projects
      const anyBaseRate = row.record.base_rates
        .map((e) => Object.values(e))
        .flat()
        .some((e) => e !== null);

      row.record.project_ids.forEach((id) => {
        const projectRate = row.record.project_rates.find((e) => e[id] !== null);

        if (!projectRate && !anyBaseRate) {
          return true;
        }
      });

      return false;
    },
    mixedRates: function (row) {
      const projectRates = row.record.project_rates;
      const uniqBaseRates = [...new Set(row.record.base_rates.map((e) => Object.values(e)).flat())];
      const trackedProjectIds = row.record.project_ids || [];
      const usedRates = new Set();

      trackedProjectIds.forEach((projectId) => {
        const pRate = this.getProjectRate(projectRates, projectId);

        if (pRate === null && uniqBaseRates.length > 1) {
          usedRates.add(uniqBaseRates[0]);
        }
        usedRates.add(pRate);
      });
      // "task_rates":[{"0":null},{"1":200.0},{"164":200.0}]
      row.record.task_rates &&
        row.record.task_rates
          .filter((e) => Object.values(e)[0] !== null)
          .forEach((e) => {
            usedRates.add(Object.values(e)[0]);
          });

      return usedRates.size > 1;
    },
    currentRate: function (row) {
      /*
        If there's a rate defined on the project we use that one as the current rate,
        if not, we use the base rate defined for that day.
        This method is always called when there's one rate either on 1 project or on the base rate.
      */
      const taskRate = row.record.task_rates ? row.record.task_rates[0] : {};
      const projectRate = row.record.project_rates[0];
      const baseRate = row.record.base_rates[0];

      return Number(Object.values(taskRate)[0] || Object.values(projectRate)[0] || Object.values(baseRate)[0]);
    },
    mixedRateListTooltip: function (row) {
      const rates = [];
      const groupedRatePerDay = [];
      const baseRates = row.record.base_rates;
      const projectRates = row.record.project_rates;
      let prevGroup = null;
      // We include base rates on the tooltip only when they were actually used.
      // If the amount is for X number of projects and all of them have a pay rate it means the base rate
      // was not used.
      const pRates = projectRates.filter((e) => Object.values(e)[0] != null).map((e) => Object.values(e)[0]);

      if (pRates.length !== row.record.project_ids.length) {
        baseRates.forEach((baseRate) => {
          const date = Object.keys(baseRate)[0];
          const rate = Object.values(baseRate)[0];

          if (prevGroup == null || prevGroup.rate !== rate) {
            const newGroup = {};

            newGroup.rate = rate;
            newGroup.since = date;
            newGroup.until = date;
            groupedRatePerDay.push(newGroup);

            prevGroup = newGroup;
          } else {
            prevGroup.until = date;
          }
        });

        groupedRatePerDay.forEach((group) => {
          let interval;

          if (group.since === group.until) {
            interval = TimeUtils.format_date(TimeUtils.parse_internal_date(group.since));
          } else {
            interval =
              TimeUtils.format_date(TimeUtils.parse_internal_date(group.since)) +
              ' - ' +
              TimeUtils.format_date(TimeUtils.parse_internal_date(group.until));
          }

          const rate = Number(group.rate).formatMoney(this.organization.currency);

          rates.push('Organization rate: ' + rate + ' (' + interval + ') \n');
        });
      }

      // We always include projects rates
      projectRates.forEach((projectRate) => {
        const projectId = parseInt(Object.keys(projectRate)[0]);
        const rate = Object.values(projectRate)[0];
        const rateValue = rate ? Number(rate).formatMoney(this.organization.currency) : 'No rate set';
        const project = this.projects.find((e) => e.id === projectId);

        if (project) {
          rates.push(htmlEscape(project.name || '') + ': ' + rateValue);
        }
      });

      row.record.task_rates?.forEach((taskRate) => {
        const taskId = Object.keys(taskRate)[0];
        const rate = Object.values(taskRate)[0];
        const taskName = this.tasks[taskId];

        if (taskName && rate) {
          rates.push(htmlEscape(taskName) + ': ' + Number(rate).formatMoney(this.organization.currency));
        }
      });

      return "<div class='text-left'>" + rates.join('<br/>') + '</div>';
    },
    recordTypeIcon: function (row) {
      const itemTypes = row.record.item_types;
      const icon = { class: '', text: '' };

      if (itemTypes.length > 1) {
        icon.class = 'a-tooltip hi hi-time-off';
        icon.text = 'Includes time off or holiday time';
      } else if (itemTypes.includes('time_off')) {
        icon.class = 'a-tooltip hi hi-time-off';
        icon.text = 'Time off hours';
      } else if (itemTypes.includes('holiday')) {
        icon.class = 'a-tooltip hi hi-time-off';
        icon.text = 'Holiday hours';
      }

      return icon;
    },
    formatDateLabel(value) {
      if (value === 'summed_date') {
        return (
          TimeUtils.format_date(TimeUtils.parse_internal_date(this.$store.state.report_query.date)) +
          ' - ' +
          TimeUtils.format_date(TimeUtils.parse_internal_date(this.$store.state.report_query.date_end))
        );
      }

      return TimeUtils.format_date(TimeUtils.parse_internal_date(value));
    },
    handleAboutChange(payload) {
      this.$emit('change', payload);
    },
  },
};
</script>
